import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DropdownItem } from '@shared/abstraction';
import { GridColumn } from '@grid/abstractions';
import { UserService } from '@shared/services/user.service';
import {
  EntityTypeService,
  InvestmentJurisdictionService,
  SecurityTypeService,
} from '../../admin/services';
import { DataType } from '@shared/helpers/enums/data-type';
import { ODataFilter } from '@shared/modules/grid/abstractions/odata-filter-column';
import { FilterTypes } from '@shared/modules/prime-grid-filters/helpers/enums/filter-type.enum';
import { KeyValue } from '@angular/common';
import { transactionTypeItems } from '../../admin/constants/transaction-type-items';
import { map, of } from 'rxjs';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import {
  Collection,
  EntityType,
  InvestmentJurisdiction,
  SecurityType,
} from '../../admin/models';
import { SecurityStatus } from '@shared/enums/security-status';
import { TaxResultService } from './tax-result.service';
import { User } from '@shared/models';
import { asyncFilterOptionsFn } from '@shared/modules/prime-grid-filters/helpers/functions/async-filter-option';

@Injectable({
  providedIn: 'root',
})
export class TaxResultHeadersService {
  private readonly translateService = inject(TranslateService);
  private readonly securityTypeService = inject(SecurityTypeService);
  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );
  private readonly entityTypeService = inject(EntityTypeService);
  private readonly userService = inject(UserService);
  private readonly taxResultService = inject(TaxResultService);

  getHeaders(): GridColumn[] {
    return [
      {
        label: this.translateService.instant('portfolio.filterTransactionType'),
        key: 'transactionTypeName',
        dataType: DataType.String,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Dropdown,
          oDataPath: 'transactionType/transactionTypeId',
          options: {
            items: of(
              transactionTypeItems
                .filter(
                  (item: DropdownItem) => item.value !== TransactionType.All
                )
                .map((item: DropdownItem) => {
                  return { value: item.value, key: item.label } as KeyValue<
                    string,
                    string
                  >;
                })
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              eq: columnFilter.getValue<KeyValue<string, string>[]>()[0].value,
            });
          },
        },
      },
      {
        label: this.translateService.instant('portfolio.securities.entityType'),
        key: 'entityTypeName',
        sortKey: 'entity.entityType.name',
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Dropdown,
          oDataPath: 'entity/entityType/name',
          options: {
            isMultiple: true,
            items: this.entityTypeService.get().pipe(
              map((entityTypeCollection: Collection<EntityType>) => {
                return entityTypeCollection.items.map(
                  (entityType: EntityType) => {
                    return {
                      value: entityType.entityTypeId
                        ? entityType.entityTypeId.toString()
                        : '',
                      key: entityType.name ? entityType.name.toString() : '',
                    } as KeyValue<string, string>;
                  }
                );
              })
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map((item: KeyValue<string, string>) => item.key),
            });
          },
        },
      },
      {
        label: this.translateService.instant(
          'portfolio.securities.securityType'
        ),
        key: 'securityTypeName',
        sortKey: 'securityType.name',
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Dropdown,
          oDataPath: 'securityType/name',
          options: {
            isMultiple: true,
            items: this.securityTypeService.activeSecurityTypeList().pipe(
              map((securityTypeCollection: Collection<SecurityType>) => {
                return securityTypeCollection.items.map(
                  (securityType: SecurityType) => {
                    return {
                      value: securityType.securityTypeId
                        ? securityType.securityTypeId.toString()
                        : '',
                      key: securityType.name
                        ? securityType.name.toString()
                        : '',
                    } as KeyValue<string, string>;
                  }
                );
              })
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map((item: KeyValue<string, string>) => item.key),
            });
          },
        },
      },
      {
        label: this.translateService.instant(
          'portfolio.securities.investmentJurisdiction'
        ),
        key: 'investingJurisdictionName',
        sortKey: 'investmentJurisdiction.country.name',
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Combobox,
          oDataPath: 'investmentJurisdiction/country/name',
          options: {
            isMultiple: true,
            allowSearch: true,
            items: asyncFilterOptionsFn(
              this.investmentJurisdictionService.countryList,
              ({ items }: Collection<InvestmentJurisdiction>) => {
                return items.map(
                  (investmentJurisdiction: InvestmentJurisdiction) => {
                    return {
                      value: investmentJurisdiction.investmentJurisdictionId
                        ? investmentJurisdiction.investmentJurisdictionId.toString()
                        : '',
                      key: investmentJurisdiction.name
                        ? investmentJurisdiction.name.toString()
                        : '',
                    } as KeyValue<string, string>;
                  }
                );
              }
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map((item: KeyValue<string, string>) => item.key),
            });
          },
        },
      },
      {
        label: this.translateService.instant('taxResult.approvedBy'),
        key: 'approvedByName',
        dataType: DataType.String,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Combobox,
          oDataPath: 'approvedByNavigation',
          options: {
            isMultiple: true,
            allowSearch: true,
            items: this.userService.getAdmins().pipe(
              map((userCollection: Collection<User>) => {
                return userCollection.items.map((user: User) => {
                  return {
                    value: user.id ?? '',
                    key:
                      user && user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : '',
                  } as KeyValue<string, string>;
                });
              })
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            const fullNames = columnFilter
              .getValue<KeyValue<string, string>[]>()
              .map(item => {
                return {
                  firstName: item.key.split(',')[1].trim(),
                  lastName: item.key.split(',')[0].trim(),
                };
              });

            columnFilter.updateQuery({
              and: [{ or: fullNames }],
            });
          },
          disableIf: () =>
            this.taxResultService.selectedTaxResultStatus() !==
            SecurityStatus.Approved,
        },
      },
      {
        label: this.translateService.instant('taxResult.lastUpdated'),
        key: 'modified',
        dataType: DataType.Date,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Datepicker,
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              ge: columnFilter.getValue<string[]>()[0],
              le: columnFilter.getValue<string[]>()[1],
            });
          },
        },
      },
    ];
  }
}
